import { Auditable, Revision } from '@mkp/shared/models';

/**
 * Maps all audit fields from a {@link Revision} DTO to their domain model counterparts.
 *
 * Handles the only real transformations between a DTO and a domain model:
 * - `createdAt`/`updatedAt`: ISO strings → `Date` objects
 * - `createdBy`/`updatedBy`: already `UserId` — passed through unchanged
 *
 * Use as a spread in any DTO → model mapper:
 * @example
 * const mapFooDtoToFoo = (dto: FooDto): Foo => ({ ...dto, ...mapAuditDates(dto) });
 */
export const mapAuditDates = (
  dto: Pick<Revision, 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>
): Auditable => ({
  createdAt: new Date(dto.createdAt),
  updatedAt: dto.updatedAt ? new Date(dto.updatedAt) : null,
  createdBy: dto.createdBy,
  updatedBy: dto.updatedBy,
});

/**
 * Utility type — converts a Revision-based DTO to its domain model shape by replacing
 * ISO string audit dates with `Date` objects (all four {@link Auditable} fields are swapped).
 *
 * Use this when the DTO and model are structurally identical apart from audit field types
 * — no need for a separate interface.
 *
 * @example
 * export type Comment = WithAuditDates<CommentDto>;
 * export const mapComment = (dto: CommentDto): Comment => ({ ...dto, ...mapAuditDates(dto) });
 */
export type WithAuditDates<
  T extends Pick<Revision, 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>,
> = Omit<T, keyof Auditable> & Auditable;
